import { Checkbox, FormControlLabel, FormGroup, Grid2 as Grid, GridSize, TextField, useMediaQuery } from "@mui/material"
import { Theme } from "@mui/material/styles"
import { Label } from "../../components/Typography/Typography"
import { useMissions } from "../../hooks/useMissions"
import {
    FieldQuicklook,
    GroundCondition,
    NoFlyReason,
    WeatherCondition,
    fieldQuicklookOptions,
    fieldQuicklookValues,
    groundConditionOptions,
    groundConditionValues,
    noFlyReasonOptions,
    noFlyReasonValues,
    weatherConditionOptions,
    weatherConditionValues,
} from "../../models/FlightConditions"
import { ToolbarOptions } from "../../store/types/AuthTypes"
import { MissionType } from "../../store/types/MissionsTypes"
import { removeOrAddCheckboxArrayEntry } from "../../utils/ArrayUtils"
import { useAuthStore } from "../../zstore/AuthStore"
import { useStyles } from "./styles"

export const MissionNotes = () => {
    const checkToolbarAccess = useAuthStore((state) => state.checkToolbarAccess)
    const { missions, isNoFlight, isFullFlight, handleMissionDataChange } = useMissions()
    const { selectedMission } = missions

    const isWidthUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"))

    const handleMissionCheckboxUpdate = <T extends keyof MissionType>(field: T, value: MissionType[T]) => {
        handleMissionDataChange({ [field]: value })
    }

    const handleNoFlyReasonUpdate = (field: NoFlyReason, checked: boolean) => {
        const noReasonArr = removeOrAddCheckboxArrayEntry(selectedMission.no_fly_selections, field, checked)
        handleMissionCheckboxUpdate("no_fly_selections", noReasonArr)
    }

    const handleWeatherConditionUpdate = (field: WeatherCondition, checked: boolean) => {
        const weatherConditionArr = removeOrAddCheckboxArrayEntry(selectedMission.weather_conditions, field, checked)
        handleMissionCheckboxUpdate("weather_conditions", weatherConditionArr)
    }

    const handleGroundConditionUpdate = (field: GroundCondition, checked: boolean) => {
        const groundConditionArr = removeOrAddCheckboxArrayEntry(selectedMission.ground_conditions, field, checked)
        handleMissionCheckboxUpdate("ground_conditions", groundConditionArr)
    }

    const handleFieldQuicklookUpdate = (field: FieldQuicklook, checked: boolean) => {
        const fieldQuicklookArr = removeOrAddCheckboxArrayEntry(
            selectedMission.field_quicklook_selections,
            field,
            checked,
        )
        handleMissionCheckboxUpdate("field_quicklook_selections", fieldQuicklookArr)
    }

    return (
        <Grid>
            <Grid container spacing={2} direction={isWidthUp ? "row" : "column"}>
                {isNoFlight && (
                    <Grid>
                        <Label disableGutter={true}>No Fly Reasons</Label>
                        <FormGroup sx={{ pb: 2 }}>
                            {noFlyReasonOptions.map((noFlyOpt) => (
                                <FormControlLabel
                                    key={noFlyOpt}
                                    label={noFlyReasonValues[noFlyOpt].displayLabel}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={selectedMission.no_fly_selections.includes(noFlyOpt)}
                                            onChange={(_, checked) => handleNoFlyReasonUpdate(noFlyOpt, checked)}
                                        />
                                    }
                                />
                            ))}
                        </FormGroup>
                    </Grid>
                )}
                <MissionNotesField field={"flight_notes"} label="General Notes" required={true} />
                <Grid size={{ md: 6 }}>
                    <Label disableGutter={true}>Weather Conditions</Label>
                    <FormGroup>
                        {weatherConditionOptions.map((weatherOpt) => (
                            <FormControlLabel
                                key={weatherOpt}
                                label={weatherConditionValues[weatherOpt].displayLabel}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={selectedMission.weather_conditions.includes(weatherOpt)}
                                        onChange={(_, checked) => handleWeatherConditionUpdate(weatherOpt, checked)}
                                    />
                                }
                            />
                        ))}
                    </FormGroup>
                </Grid>
                <Grid size={{ md: 6 }}>
                    <Label disableGutter={true}>Ground Conditions</Label>
                    <FormGroup>
                        {groundConditionOptions.map((groundOpt) => (
                            <FormControlLabel
                                key={groundOpt}
                                label={groundConditionValues[groundOpt].displayLabel}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={selectedMission.ground_conditions.includes(groundOpt)}
                                        onChange={(_, checked) => handleGroundConditionUpdate(groundOpt, checked)}
                                    />
                                }
                            />
                        ))}
                    </FormGroup>
                </Grid>
                {isFullFlight && <MissionNotesField field={"sensor_notes"} label="Sensor Notes" disabled={true} />}
                <MissionNotesField field={"error_notes"} label="Error Notes" grid={6} />
                <MissionNotesField field={"error_action"} label="Action Taken" grid={6} />
                <Grid>
                    <Label disableGutter={true}>Field Quicklook</Label>
                    <FormGroup>
                        {fieldQuicklookOptions.map((quicklookOpt) => (
                            <FormControlLabel
                                key={quicklookOpt}
                                label={fieldQuicklookValues[quicklookOpt].displayLabel}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={selectedMission.field_quicklook_selections.includes(quicklookOpt)}
                                        onChange={(_, checked) => handleFieldQuicklookUpdate(quicklookOpt, checked)}
                                    />
                                }
                            />
                        ))}
                    </FormGroup>
                </Grid>
                <MissionNotesField field={"tomorrow_notes"} label="Tomorrow's Plan" required={true} />
                <MissionNotesField
                    field={"flight_blog"}
                    label="Add Blog Entry (Managers Only)"
                    disabled={!checkToolbarAccess([ToolbarOptions.Edit_Blog_Entries, ToolbarOptions.Mission_Admin])}
                />
            </Grid>
        </Grid>
    )
}

interface MissionNotesFieldProps<T extends keyof MissionType> {
    field: T
    label: string
    grid?: GridSize
    required?: boolean
    disabled?: boolean
}

const MissionNotesField = <T extends keyof MissionType>(props: MissionNotesFieldProps<T>) => {
    const { field, grid, required, label, disabled } = props

    const { classes, cx } = useStyles()
    const { missions, handleMissionDataChange } = useMissions()
    const { selectedMission } = missions

    return (
        <Grid size={{ md: grid ?? 12 }}>
            <TextField
                label={label}
                value={selectedMission[field] ?? ""}
                onChange={(e) => {
                    handleMissionDataChange({ [field]: e.target.value })
                }}
                onBlur={(e) => {
                    handleMissionDataChange({ [field]: e.target.value?.trim() })
                }}
                className={cx(required && classes.editorNotesHelperText)}
                helperText={required && "Required"}
                error={required && !selectedMission[field]}
                multiline={true}
                fullWidth={true}
                variant={"outlined"}
                minRows={4}
                disabled={disabled}
            />
        </Grid>
    )
}
